/* ==============================================================================
   Gráficos
   ==============================================================================
   SVG a mão, sem biblioteca. Não é orgulho: são duas formas — uma área de série
   única e uma barra horizontal — e qualquer biblioteca de gráficos custa entre
   50 e 200 kB comprimidos para desenhar um `<path>` e um `<rect>`. Numa
   aplicação cujo primeiro requisito é caber numa VM apertada, isso é caro pelo
   que entrega.

   AS ESCOLHAS DE FORMA, E POR QUE CADA UMA
   ========================================

   Série ÚNICA, sempre
   -------------------
   Cada gráfico plota uma coisa só. Cadastros, mensagens e chamadas são três
   gráficos e não três linhas num só — porque as grandezas são de ordens
   diferentes (dezenas de mensagens, unidades de cadastro), e o único jeito de
   as pôr no mesmo desenho seria dois eixos verticais.

   Dois eixos verticais é o erro de gráfico mais comum que existe: o alinhamento
   entre as duas escalas é ARBITRÁRIO, então o desenho inventa uma correlação
   que não está no dado. Três gráficos pequenos lado a lado respondem a mesma
   pergunta sem mentir.

   Sendo série única, não há legenda: não existe identidade a distinguir, e uma
   caixinha com um quadrado só repete o título e come espaço.

   UMA cor por gráfico, e ela é o acento do tema
   ---------------------------------------------
   Não há rampa por valor. Colorir a barra mais escura porque ela é maior
   duplica no matiz o que o comprimento já diz, e gasta o único canal livre em
   informação redundante.

   Os servidores são categorias NOMINAIS — não têm ordem natural — então todas
   as barras usam a mesma cor. Quem quer saber qual é maior lê o comprimento,
   que é para isso que a barra existe.

   O texto NUNCA veste a cor da série
   ----------------------------------
   Rótulos, valores e eixos usam os tokens de texto. O acento em amarelo ou
   ciano é ilegível como texto sobre a superfície, e a identidade já vem da
   marca colorida ao lado. Esta regra é o que mantém o gráfico legível quando o
   usuário troca o acento do tema.

   Marcas finas, grade recessiva
   -----------------------------
   Linha de 2px, ponta arredondada; preenchimento de área a ~12% de opacidade —
   uma lavagem, nunca um bloco saturado; grade de 1px sólida, um passo acima da
   superfície. O dado é a única coisa com permissão para ser forte.
============================================================================== */

import { useId, useState, type ReactNode } from 'react';

/* ==============================================================================
   Área de série única
============================================================================== */

export interface AreaPoint {
  /** Rótulo do eixo horizontal. Ex.: `2026-09-01`. */
  label: string;
  value: number;
}

export function AreaChart({
  points,
  height = 96,
  formatValue = (v) => v.toLocaleString('pt-BR'),
  formatLabel = (l) => l,
}: {
  points: ReadonlyArray<AreaPoint>;
  height?: number;
  formatValue?: (value: number) => string;
  formatLabel?: (label: string) => string;
}) {
  const gradientId = useId();
  const [hover, setHover] = useState<number | null>(null);

  if (points.length === 0) {
    return <EmptyPlot height={height}>Sem dados no período.</EmptyPlot>;
  }

  /*
   * O topo é o maior valor, com no MÍNIMO 1.
   *
   * Sem o piso, uma série toda zerada daria `max = 0` e uma divisão por zero em
   * cada ponto — `NaN` no `d` do path, e o navegador simplesmente não desenha
   * nada. Um gráfico vazio por divisão por zero é indistinguível de um gráfico
   * vazio por falta de dado, e os dois exigem investigações diferentes.
   */
  const max = Math.max(1, ...points.map((p) => p.value));

  /*
   * Coordenadas em 0..100 nos dois eixos, com `preserveAspectRatio="none"`.
   *
   * O SVG estica para a largura do contêiner sem que nada aqui precise saber
   * quantos pixels ele tem. A alternativa — medir o contêiner com
   * `ResizeObserver` — resolveria o mesmo problema com um observador e um
   * render a mais por redimensionamento.
   */
  const stepX = points.length > 1 ? 100 / (points.length - 1) : 0;
  const coords = points.map((p, i) => ({
    x: i * stepX,
    y: 100 - (p.value / max) * 100,
  }));

  const line = coords
    .map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(2)},${c.y.toFixed(2)}`)
    .join(' ');
  const area = `${line} L100,100 L0,100 Z`;

  const ativo = hover !== null ? points[hover] : null;
  const ativoCoord = hover !== null ? coords[hover] : null;

  return (
    <div className="relative" style={{ height }}>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="block h-full w-full"
        role="img"
        aria-label={`Série de ${points.length} dias. Máximo ${formatValue(max)}.`}
      >
        <defs>
          {/*
            Degradê vertical em vez de opacidade uniforme.
            ----------------------------------------------
            A lavagem some perto da linha de base, o que evita a borda dura
            horizontal que um preenchimento chapado cria — e essa borda lê como
            um eixo que não existe.
          */}
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-accent)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--color-accent)" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Grade: três linhas, recessivas. Só o suficiente para dar altura ao
            olho — mais linhas competiriam com o dado. */}
        {[25, 50, 75].map((y) => (
          <line
            key={y}
            x1="0"
            y1={y}
            x2="100"
            y2={y}
            stroke="var(--color-border-subtle)"
            strokeWidth="0.4"
            vectorEffect="non-scaling-stroke"
          />
        ))}

        <path d={area} fill={`url(#${gradientId})`} />
        <path
          d={line}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          /*
           * `non-scaling-stroke` é obrigatório com `preserveAspectRatio="none"`.
           *
           * Sem ele o esticamento horizontal deforma a espessura: a linha fica
           * grossa nos trechos verticais e fina nos horizontais, e o gráfico
           * parece desenhado à mão trêmula.
           */
          vectorEffect="non-scaling-stroke"
        />

        {ativoCoord && (
          <line
            x1={ativoCoord.x}
            y1="0"
            x2={ativoCoord.x}
            y2="100"
            stroke="var(--color-accent)"
            strokeWidth="1"
            strokeOpacity="0.5"
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>

      {/*
        Faixas de captura em HTML, sobre o SVG.
        ---------------------------------------
        O alvo de toque de cada dia é uma fatia da largura inteira — não o ponto
        do gráfico. Com 30 pontos num painel de 300px, cada ponto tem 10px de
        largura e alguns pixels de altura; mirar nisso é impossível no trackpad
        e mais ainda no toque. A faixa vertical inteira sempre acerta.
      */}
      <div className="absolute inset-0 flex">
        {points.map((p, i) => (
          <button
            key={p.label}
            type="button"
            className="h-full flex-1"
            style={{ background: 'transparent' }}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            onFocus={() => setHover(i)}
            onBlur={() => setHover(null)}
            aria-label={`${formatLabel(p.label)}: ${formatValue(p.value)}`}
          />
        ))}
      </div>

      {ativo && ativoCoord && (
        <div
          className="glass pointer-events-none absolute px-2 py-1"
          style={{
            /*
             * A dica gruda na borda quando o ponto está perto dela.
             *
             * `translateX(-50%)` sozinho jogaria metade da caixa para fora do
             * painel no primeiro e no último dia — que são exatamente os dois
             * pontos que mais se consulta.
             */
            left: `${Math.min(88, Math.max(12, ativoCoord.x))}%`,
            transform: 'translateX(-50%)',
            bottom: '100%',
            marginBottom: 4,
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--glass-edge), 0 6px 18px oklch(0% 0 0 / 0.45)',
            whiteSpace: 'nowrap',
            zIndex: 1,
          }}
        >
          <span
            className="block font-semibold tabular-nums"
            style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)' }}
          >
            {formatValue(ativo.value)}
          </span>
          <span
            className="block"
            style={{ fontSize: 9, color: 'var(--color-text-tertiary)' }}
          >
            {formatLabel(ativo.label)}
          </span>
        </div>
      )}
    </div>
  );
}

/* ==============================================================================
   Barras horizontais
   ==============================================================================
   Horizontal e não vertical porque os rótulos são NOMES — de servidor, de
   tabela. Nome em coluna vertical ou vira diagonal ou trunca, e as duas saídas
   custam a leitura do rótulo, que é metade da informação.
============================================================================== */

export interface BarItem {
  id: string;
  label: string;
  value: number;
  /** Segunda linha do rótulo, opcional. */
  hint?: string;
}

export function BarList({
  items,
  formatValue = (v) => v.toLocaleString('pt-BR'),
}: {
  items: ReadonlyArray<BarItem>;
  formatValue?: (value: number) => string;
}) {
  if (items.length === 0) {
    return <EmptyPlot height={72}>Nada para mostrar ainda.</EmptyPlot>;
  }

  const max = Math.max(1, ...items.map((i) => i.value));

  return (
    <div className="flex flex-col" style={{ gap: 'var(--spacing-2)' }}>
      {items.map((item) => (
        <div key={item.id}>
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <span
              className="min-w-0 truncate"
              style={{
                fontSize: 'var(--text-xs)',
                /* Token de TEXTO, nunca a cor da barra. A identidade vem da
                   barra logo abaixo. */
                color: 'var(--color-text-secondary)',
              }}
            >
              {item.label}
              {item.hint !== undefined && (
                <span
                  style={{ marginLeft: 6, color: 'var(--color-text-tertiary)' }}
                >
                  {item.hint}
                </span>
              )}
            </span>
            <span
              className="shrink-0 tabular-nums font-semibold"
              style={{
                fontSize: 'var(--text-xs)',
                fontFamily: 'var(--font-mono)',
                color: 'var(--color-text-primary)',
              }}
            >
              {formatValue(item.value)}
            </span>
          </div>

          {/* Trilho + barra. O trilho dá a escala: sem ele, "12" numa barra
              curta não diz se é pouco em relação a quê. */}
          <div
            style={{
              height: 8,
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-surface-sunken)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                /*
                 * Mínimo de 2% para um valor não-zero.
                 *
                 * Uma barra de 0,3px é invisível, e "invisível" e "zero" têm de
                 * ser distinguíveis — senão o servidor com 1 membro parece
                 * vazio.
                 */
                width:
                  item.value === 0 ? 0 : `${Math.max(2, (item.value / max) * 100)}%`,
                height: '100%',
                borderRadius: 'var(--radius-full)',
                background: 'var(--color-accent-solid)',
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ==============================================================================
   Peças
============================================================================== */

function EmptyPlot({
  height,
  children,
}: {
  height: number;
  children: ReactNode;
}) {
  return (
    <div
      className="grid place-items-center"
      style={{
        height,
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface-sunken)',
        fontSize: 'var(--text-2xs)',
        color: 'var(--color-text-tertiary)',
      }}
    >
      {children}
    </div>
  );
}
